import React, { useMemo } from "react";
import { Divider } from "@bookingcom/bui-react";
import { getFeaturesToRender } from "../../utils/features.js";
import FareFeature from "../FareFeature/index.js";
import styles from "./FareFeatures.module.css";
import Frame from "../../../components/elements/Frame/index.js";
import FareFeatureHeading from "./FareFeatureHeading.js";
import { CHANGE_FEATURES } from "@flights/branded-fares";
import useFareFeaturesHeading from "../../hooks/useFareFeaturesHeading.js";
const FareFeatures = ({ brandedFareInfo, sellableFeaturesRequiredByAirline, excludedFeaturesRequiredByAirline = false, showHeader, showDivider, isPreCheckAndPay, showDescriptions, showSubtitles, compactHeight = false, showExcludedFeatures = true, showChangeDisclaimerStar, uniqueUpsellFeatures = [], showUpsellFeatureUpgradeIcon, includedProducts, isTabularBFVariant }) => {
    const heading = useFareFeaturesHeading();
    if (!brandedFareInfo)
        throw new Error("FareSwitcher: missing branded fare info");
    const featuresToRender = useMemo(() => {
        return getFeaturesToRender(brandedFareInfo, {
            includeSellableFeatures: isTabularBFVariant ? false : sellableFeaturesRequiredByAirline,
            includeExcludedFeatures: excludedFeaturesRequiredByAirline,
            isPreCheckAndPay,
            isTabularBFVariant
        });
    }, [
        brandedFareInfo,
        sellableFeaturesRequiredByAirline,
        isPreCheckAndPay,
        excludedFeaturesRequiredByAirline,
        isTabularBFVariant
    ]);
    const allIncludedFeatures = featuresToRender.includedFeatures;
    const includedAndSellableFeatures = allIncludedFeatures;
    const hasIncludedOrSellableFeatures = includedAndSellableFeatures.length > 0;
    const excludedFeatures = showExcludedFeatures ? featuresToRender.nonIncludedFeatures : [];
    const hasExcludedFeatures = excludedFeatures.length > 0;
    const checkedInLuggageContent = useMemo(() => {
        if (!includedProducts?.segments?.[0])
            return null;
        return includedProducts.segments[0].find((item) => item.luggageType === "CHECKED_IN")?.content;
    }, [includedProducts]);
    if (!hasIncludedOrSellableFeatures && !hasExcludedFeatures)
        return null;
    const getFeatureContent = (feature) => {
        if (feature.featureName === "CHECK_BAGGAGE" && checkedInLuggageContent?.featureLabel) {
            return checkedInLuggageContent.featureLabel;
        }
        const disclaimerStar = showChangeDisclaimerStar && CHANGE_FEATURES.includes(feature.featureName) ? "*" : "";
        return (feature.label ?? "") + disclaimerStar;
    };
    const getFeatureSubtitle = (feature) => {
        if (!showSubtitles)
            return undefined;
        if (feature.featureName === "CHECK_BAGGAGE" && checkedInLuggageContent?.subtitle) {
            return checkedInLuggageContent.subtitle;
        }
        return feature.subtitle;
    };
    return (React.createElement(React.Fragment, null,
        showHeader && hasIncludedOrSellableFeatures && React.createElement(FareFeatureHeading, null, heading),
        React.createElement("div", { role: "list" }, includedAndSellableFeatures.map((feature) => (React.createElement(FareFeature, { key: feature.featureName, id: feature.featureName, IconSVG: feature.icon, content: getFeatureContent(feature), description: showDescriptions ? feature.description : undefined, subtitle: getFeatureSubtitle(feature), availability: feature.availability, compactHeight: compactHeight, isUniqueUpsellFeature: uniqueUpsellFeatures.some((upsellFeature) => upsellFeature.featureName === feature.featureName), showUpsellFeatureUpgradeIcon: showUpsellFeatureUpgradeIcon })))),
        showDivider && hasExcludedFeatures && hasIncludedOrSellableFeatures && (React.createElement(Frame, { mb: 4 },
            React.createElement(Divider, { className: styles.divider, attributes: { "data-testid": "fareFeatureDivider" } }))),
        React.createElement("div", { role: "list" }, excludedFeatures.map((feature) => (React.createElement(FareFeature, { key: feature.featureName, id: feature.featureName, content: feature.label, availability: feature.availability, IconSVG: feature.icon, compactHeight: compactHeight }))))));
};
export default FareFeatures;
